import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Loader2, CalendarDays, User } from "lucide-react";
import Seo from "@/components/Seo";
import CtaStrip from "@/components/CtaStrip";
import NotFoundPage from "@/pages/NotFoundPage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { COMPLIANCE_LINE_SHORT, BUSINESS } from "@/lib/constants";
import { fetchBlogPost } from "@/lib/api";

const fmt = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
    } catch { return ""; }
};

const MD_COMPONENTS = {
    h2: (props) => <h2 className="font-serif text-3xl text-[#1A362B] tracking-tight mt-12 mb-4" {...props} />,
    h3: (props) => <h3 className="font-serif text-2xl text-[#1A362B] tracking-tight mt-8 mb-3" {...props} />,
    p: (props) => <p className="text-[#1C1C1C]/80 leading-relaxed my-4" {...props} />,
    ul: (props) => <ul className="list-disc pl-6 space-y-2 my-4 text-[#1C1C1C]/80" {...props} />,
    ol: (props) => <ol className="list-decimal pl-6 space-y-2 my-4 text-[#1C1C1C]/80" {...props} />,
    li: (props) => <li className="leading-relaxed" {...props} />,
    a: (props) => <a className="text-[#1A362B] underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition" {...props} />,
    strong: (props) => <strong className="text-[#1A362B] font-semibold" {...props} />,
    blockquote: (props) => <blockquote className="border-l-2 border-[#C69C6D] pl-6 my-8 py-1 text-[#1A362B] font-serif text-xl italic" {...props} />,
    em: (props) => <em className="text-[#1C1C1C]/60 text-sm not-italic block mt-8 pt-6 border-t border-border" {...props} />,
    table: (props) => <div className="overflow-x-auto my-6"><table className="w-full text-sm border-collapse" {...props} /></div>,
    th: (props) => <th className="text-left bg-[#F3F1EA] text-[#1A362B] font-semibold px-4 py-3 border border-border" {...props} />,
    td: (props) => <td className="px-4 py-3 border border-border text-[#1C1C1C]/80" {...props} />,
};


export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | ok | notfound

    useEffect(() => {
        setStatus("loading");
        fetchBlogPost(slug)
            .then((p) => { setPost(p); setStatus("ok"); })
            .catch(() => setStatus("notfound"));
    }, [slug]);

    if (status === "loading") {
        return (
            <div className="pt-24 bg-background min-h-screen">
                <div className="container-prose py-24 text-center text-[#1C1C1C]/50"><Loader2 className="w-6 h-6 animate-spin inline-block" /></div>
            </div>
        );
    }
    if (status === "notfound") return <NotFoundPage />;

    const articleLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.meta_description || post.excerpt,
        "image": post.cover_image ? [post.cover_image] : undefined,
        "datePublished": post.created_at,
        "dateModified": post.updated_at,
        "author": { "@type": "Person", "name": post.author },
        "publisher": { "@id": "https://financewealth.com.au/#business" },
        "mainEntityOfPage": `https://financewealth.com.au/blog/${post.slug}`
    };

    const faqs = Array.isArray(post.faqs) ? post.faqs : [];
    const faqLd = faqs.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
    } : null;
    const jsonLd = faqLd ? [articleLd, faqLd] : articleLd;

    return (
        <div data-testid="page-blog-post" className="pt-20">
            <Seo
                title={`${post.title} | Finance Wealth`}
                description={post.meta_description || post.excerpt}
                keywords={post.keywords}
                canonicalPath={`/blog/${post.slug}`}
                ogImage={post.cover_image}
                jsonLd={jsonLd}
            />

            <article>
                <header className="bg-[#F3F1EA]">
                    <div className="container-prose py-16 md:py-24 max-w-4xl">
                        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm text-[#1A362B] hover:text-[#C69C6D] transition mb-8">
                            <ArrowLeft className="w-4 h-4" /> All articles
                        </Link>
                        <div className="flex items-center gap-4 text-xs">
                            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#C69C6D]">{post.category}</span>
                            <span className="text-[#1C1C1C]/50 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {fmt(post.created_at)}</span>
                            <span className="text-[#1C1C1C]/50 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {post.author}</span>
                        </div>
                        <h1 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A362B] font-medium">{post.title}</h1>
                        {post.excerpt && <p className="mt-6 text-lg text-[#1C1C1C]/70 leading-relaxed font-light">{post.excerpt}</p>}
                    </div>
                </header>

                {post.cover_image && (
                    <div className="container-prose max-w-4xl -mt-2">
                        <div className="aspect-[16/8] overflow-hidden rounded-sm -translate-y-8 shadow-lg">
                            <img src={post.cover_image} alt={post.cover_image_alt || post.title} className="w-full h-full object-cover" />
                        </div>
                    </div>
                )}

                <div className="bg-background">
                    <div className="container-prose max-w-3xl pb-24">
                        <div
                            data-testid="blog-content"
                            className="prose-fw"
                        >
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                                {post.content}
                            </ReactMarkdown>

                            {faqs.length > 0 && (
                                <div data-testid="blog-faq" className="mt-14">
                                    <h2 className="font-serif text-3xl text-[#1A362B] tracking-tight mb-4">Frequently asked questions</h2>
                                    <Accordion type="single" collapsible className="w-full">
                                        {faqs.map((f, i) => (
                                            <AccordionItem key={i} value={`blog-faq-${i}`} data-testid={`blog-faq-item-${i}`}>
                                                <AccordionTrigger className="font-serif text-lg text-[#1A362B] text-left">{f.q}</AccordionTrigger>
                                                <AccordionContent className="text-[#1C1C1C]/75 leading-relaxed">{f.a}</AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </div>
                            )}

                            <div data-testid="blog-compliance" className="mt-14 border-t border-border pt-8">
                                <p className="text-xs text-[#1C1C1C]/55 leading-relaxed italic border-l-2 border-[#C69C6D] pl-3">
                                    {COMPLIANCE_LINE_SHORT}. {BUSINESS.disclaimer}
                                </p>
                                <p className="mt-4 text-sm text-[#1C1C1C]/70 leading-relaxed">
                                    Call Gurpreet on{" "}
                                    <a href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`} className="text-[#1A362B] font-medium underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition">{BUSINESS.phone}</a>
                                    {" "}or <Link to="/contact" className="text-[#1A362B] font-medium underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition">book a free consultation</Link> at financewealth.com.au/contact
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            <CtaStrip />
        </div>
    );
}
